import { z } from 'zod';

import { EnvironmentError } from '@/src/lib/environment';

import { Auth0EnvironmentSchema, parseAuth0ProcessEnv } from './auth0';
import { EnvironmentSchema as DeploymentEnvironmentSchema } from './deployment-env';
import { DocumentIntelligenceModelId } from './document-intelligence';
import { InternalApiBearerTokenSchema } from './internal-api';
import { PublicStaticClientEnvironmentSchema } from './public';
import { UrlSchema } from './urls';
import { OptionalStringVariableSchema, RequiredStringVariableSchema } from './util';

const EnvironmentSchema = PublicStaticClientEnvironmentSchema.extend({
  auth0: Auth0EnvironmentSchema,
  connectGraphqlUrl: UrlSchema,
  connectUiUrl: UrlSchema,
  craftApi: z.object({
    m2mTokenAudience: OptionalStringVariableSchema,
    m2mTokenSecret: OptionalStringVariableSchema,
  }),
  craftInternalApi: z.object({
    bearerToken: InternalApiBearerTokenSchema,
  }),
  credentialEngine: z.object({
    apiUrl: OptionalStringVariableSchema,
    credentialFinderUrl: OptionalStringVariableSchema,
  }),
  database: z.object({
    databaseUrl: z.string().min(1).url(),
  }),
  deploymentEnv: DeploymentEnvironmentSchema,
  documentIntelligence: z.object({
    doclingUrl: OptionalStringVariableSchema.pipe(z.string().url().optional()),
    modelId: z
      .nativeEnum(DocumentIntelligenceModelId)
      .default(DocumentIntelligenceModelId.OpenAIGpt54Nano),
    temperature: z.preprocess(
      val => (val ? Number(val) : undefined),
      z.number().min(0).max(2).optional(),
    ),
  }),
  edlink: z.object({
    clientId: RequiredStringVariableSchema,
    clientSecret: RequiredStringVariableSchema,
  }),
  edlinkDataSync: z.object({
    clientId: OptionalStringVariableSchema,
    clientSecret: OptionalStringVariableSchema,
    serviceAccountToken: OptionalStringVariableSchema,
    teamId: OptionalStringVariableSchema,
  }),
  googleMaps: z.object({
    addressValidationUrl: OptionalStringVariableSchema,
    apiKey: OptionalStringVariableSchema,
  }),
  isJestTest: z
    .string()
    .optional()
    .transform(val => typeof val === 'string' && val.trim().length !== 0),
  logLevel: z.enum(['warn', 'error', 'info', 'debug']).optional(),
  ltiSecretKey: z
    .string()
    .base64('LTI_SECRET_KEY must be a valid base64 string')
    .refine(val => Buffer.from(val, 'base64').length === 32, {
      message: 'LTI_SECRET_KEY must decode to exactly 32 bytes (use `openssl rand -base64 32`)',
    }),
  mapMyFuture: z.object({
    apiBaseUrl: z.string().url('Map My Future API URL must be a valid URL'),
    badgeEndpointPath: z.string(),
    secretKey: z.string(),
  }),
  metabase: z.object({
    secretKey: RequiredStringVariableSchema,
  }),
  mui: z.object({
    premiumKey: RequiredStringVariableSchema,
  }),
  reporting: z.object({
    key: OptionalStringVariableSchema,
    url: OptionalStringVariableSchema,
  }),
  rudderstack: z.object({
    dataPlaneUrl: OptionalStringVariableSchema,
    nodeSdkWriteKey: RequiredStringVariableSchema,
  }),
  statsig: z.object({
    serverSdkKey: z.string().startsWith('secret-'),
  }),
  thoughtspot: z.object({
    baseUrl: z.string().min(1).url(),
    embedTokenTtlSeconds: z.preprocess(
      val => (val ? Number(val) : undefined),
      z.number().min(1).optional(),
    ),
    orgId: RequiredStringVariableSchema,
    secretKey: RequiredStringVariableSchema,
    serviceUsername: RequiredStringVariableSchema,
  }),
  usps: z.object({
    addressValidationApiUrl: OptionalStringVariableSchema,
    clientId: OptionalStringVariableSchema,
    clientSecret: OptionalStringVariableSchema,
    tokenUrl: OptionalStringVariableSchema,
  }),
  velocity: z.object({
    webhookKey: OptionalStringVariableSchema,
  }),
});

type _EnvironmentVarsInput = z.input<typeof EnvironmentSchema>;

type EnvironmentVarsInput<T = _EnvironmentVarsInput> = {
  [key in keyof T]?: T[key] extends Record<string, unknown>
    ? EnvironmentVarsInput<T[key]>
    : string | undefined;
};

export type EnvironmentVars = z.infer<typeof EnvironmentSchema>;

let CachedEnvironment: EnvironmentVars | null = null;

/**
 * Retrieves the environment variables from the associated .env.* files and validates them based
 * on the schema, {@link EnvironmentSchema}.
 *
 * The parsed result is memoized at module scope and parsed only once per process. Under the
 * build-once-deploy-many model the values are injected at process start and never mutate during
 * a process's lifetime, so the cache can never go stale and is safe to reuse on every call.
 *
 * Tests that mutate {@link process.env} between cases must call {@link resetEnvironmentCache}
 * to clear the memoized value so the next call re-parses the updated variables.
 *
 * @example
 * const environment = getEnvironment().auth0.baseUrl
 * @example
 * const { auth0, database, deploymentEnv, logLevel } = getEnvironment();
 *
 * @throws {z.ZodError} If the environment variables are invalid.
 *
 * @returns {EnvironmentVars} The type-safe, validated environment variables.
 */
export const getEnvironment = (): EnvironmentVars => {
  if (CachedEnvironment !== null) {
    return CachedEnvironment;
  }
  const parsed = EnvironmentSchema.safeParse({
    auth0: parseAuth0ProcessEnv(),
    connectGraphqlUrl: process.env.CONNECT_GRAPHQL_URL,
    connectUiUrl: process.env.CONNECT_UI_URL,
    craftApi: {
      m2mTokenAudience: process.env.CRAFT_API_M2M_AUDIENCE,
      m2mTokenSecret: process.env.CRAFT_API_M2M_SECRET,
    },
    craftInternalApi: {
      bearerToken: process.env.CRAFT_INTERNAL_API_BEARER_TOKEN,
    },
    credentialEngine: {
      apiUrl: process.env.CREDENTIAL_ENGINE_API_URL,
      credentialFinderUrl: process.env.CREDENTIAL_FINDER_BASE_URL,
    },
    database: {
      databaseUrl: process.env.DATABASE_URL,
    },
    deploymentEnv: process.env.DEPLOYMENT_ENV,
    documentIntelligence: {
      doclingUrl: process.env.DOCLING_URL,
      modelId: process.env.AI_MODEL_ID,
      temperature: process.env.AI_TEMPERATURE,
    },
    edlink: {
      clientId: process.env.EDLINK_CLIENT_ID,
      clientSecret: process.env.EDLINK_CLIENT_SECRET,
    },
    edlinkDataSync: {
      clientId: process.env.EDLINK_DATA_APPLICATION_ID,
      clientSecret: process.env.EDLINK_DATA_APPLICATION_SECRET,
      serviceAccountToken: process.env.EDLINK_SERVICE_ACCOUNT_TOKEN,
      teamId: process.env.EDLINK_TEAM_ID,
    },
    googleMaps: {
      addressValidationUrl: process.env.GOOGLE_MAPS_ADDRESS_VALIDATION_URL,
      apiKey: process.env.GOOGLE_MAPS_API_KEY,
    },
    idleUserWatcherEnabled: process.env.NEXT_PUBLIC_IDLE_USER_WATCHER_ENABLED,
    isJestTest: process.env.JEST_WORKER_ID,
    logLevel: process.env.LOG_LEVEL,
    ltiSecretKey: process.env.LTI_SECRET_KEY,
    mapMyFuture: {
      apiBaseUrl: process.env.MAP_MY_FUTURE_API_URL,
      badgeEndpointPath: process.env.MAP_MY_FUTURE_BADGE_ENDPOINT_PATH,
      secretKey: process.env.MAP_MY_FUTURE_SECRET_KEY,
    },
    metabase: {
      secretKey: process.env.METABASE_SECRET_KEY,
    },
    mui: {
      premiumKey: process.env.MUI_PREMIUM_KEY,
    },
    reporting: {
      key: process.env.REPORTING_API_KEY,
      url: process.env.REPORTING_API_URL,
    },
    rudderstack: {
      dataPlaneUrl: process.env.RUDDERSTACK_DATAPLANE_URL,
      nodeSdkWriteKey: process.env.RUDDERSTACK_NODE_SDK_WRITE_KEY,
    },
    statsig: {
      serverSdkKey: process.env.STATSIG_SERVER_SDK_KEY,
    },
    thoughtspot: {
      baseUrl: process.env.THOUGHTSPOT_BASE_URL,
      embedTokenTtlSeconds: process.env.THOUGHTSPOT_EMBED_TOKEN_TTL_SECONDS,
      orgId: process.env.THOUGHTSPOT_ORG_ID,
      secretKey: process.env.THOUGHTSPOT_SECRET_KEY,
      serviceUsername: process.env.THOUGHTSPOT_SERVICE_USERNAME,
    },
    usps: {
      addressValidationApiUrl: process.env.USPS_ADDRESS_VALIDATION_API_URL,
      clientId: process.env.USPS_API_CLIENT_ID,
      clientSecret: process.env.USPS_API_CLIENT_SECRET,
      tokenUrl: process.env.USPS_API_TOKEN_URL,
    },
    velocity: {
      webhookKey: process.env.VELOCITY_WEBHOOK_KEY,
    },
  } satisfies EnvironmentVarsInput);

  if (parsed.error) {
    const err = EnvironmentError.fromZodError(parsed.error);
    /* eslint-disable-next-line no-console -- This module is imported in various runtimes and
       importing the logger here can cause issues in edge runtimes. */
    console.error(err.message);
    throw err;
  }
  CachedEnvironment = parsed.data;
  return CachedEnvironment;
};

/**
 * Clears the process-scoped {@link CachedEnvironment} so the next {@link getEnvironment} call
 * re-parses {@link process.env}.
 *
 * Environment variables are immutable for a process's lifetime, so this reset is intended for
 * test isolation - suites that mutate {@link process.env} between cases - and not for production
 * use.
 */
export const resetEnvironmentCache = (): void => {
  CachedEnvironment = null;
};
